/** Routes, and the gate in front of each screen: session, password, MFA, role. */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { BlotterDetailPage, BlotterNewPage, BlotterPage, TanodPage } from './peace/BlotterPages';
import { HelpPage } from './help/HelpPage';
import { ConsolidatedPage, PrivacyPage, ReportLibraryPage } from './privacy/PrivacyPages';
import { PortalEntryPage, PortalHomePage } from './portal/PortalPages';
import { CitizenAccountsPage, FeedbackPage } from './portal/PortalAdminPages';
import { AnnouncementsPage, TransparencyPage } from './portal/CommsPages';
import { AssistancePage, CentersPage, DistributionPage, DistributionsPage, FundPage, IncidentPage, IncidentsPage, ProgramPage, ProgramsPage, ZonePage, ZonesPage } from './social/SocialPages';
import { AssetPage, CountPage, CountsPage, ProcurementPage, ProjectPage, ProjectsPage, PropertyPage } from './property/PropertyPages';
import { AssembliesPage, AssemblyDetailPage, CommitteesPage, LegislationDetailPage, LegislationListPage, SessionDetailPage, SessionsPage } from './legislation/LegislationPages';
import { BookletsPage, BudgetPage, CashbookPage, CollectionsPage, DepositsPage, FdpPage, IncomeAdvancesPage, VouchersPage } from './treasury/TreasuryPages';
import { RequireGrant, VawDetailPage, VawListPage, VawNewPage, VawReportPage } from './restricted/VawPages';
import { BadacPage, BadacReportPage } from './restricted/BadacPages';
import { KpDetailPage, KpListPage, KpNewPage, KpReportPage } from './peace/KpPages';
import { useAuth } from '../lib/auth';
import { Alert, Skeleton } from '../components/ui';
import { AppShell } from './AppShell';
import { SupportAccessPage } from './setup/DataPages';
import { AccountPage } from './AccountPage';
import { LoginPage } from './LoginPage';
import { ChangePasswordPage } from './ChangePasswordPage';
import { MfaSetupPage } from './MfaSetupPage';
import { DashboardPage } from './DashboardPage';
import { BarangayPage } from './setup/BarangayPage';
import { OfficialsPage } from './setup/OfficialsPage';
import { UsersPage } from './setup/UsersPage';
import { RolesPage } from './setup/RolesPage';
import { AuditLogPage } from './setup/AuditLogPage';
import { RestrictedAccessPage } from './RestrictedAccessPage';
import { BarangaysPage } from './platform/BarangaysPage';
import { ResidentsPage } from './residents/ResidentsPage';
import { ResidentFormPage } from './residents/ResidentFormPage';
import { ResidentDetailPage } from './residents/ResidentDetailPage';
import { HouseholdDetailPage, HouseholdsPage } from './residents/HouseholdsPages';
import { RegistryReportsPage } from './residents/RegistryReportsPage';
import { ImportPage } from './residents/ImportPage';
import { VerifyIdPage } from './VerifyIdPage';
import { VerifyDocPage } from './VerifyDocPage';
import { DocumentDetailPage, DocumentsPage, NewDocumentPage } from './documents/DocumentsPages';
import { BusinessesPage } from './documents/BusinessesPage';
import { DocumentSetupPage } from './documents/DocumentSetupPage';

function Protected({ children, permissions, platform }: { children: ReactNode; permissions?: string[]; platform?: boolean }): JSX.Element {
  const { user, loading, can } = useAuth();
  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!user) {
    try {
      if (window.location.pathname !== '/') sessionStorage.setItem('cx_return', window.location.pathname + window.location.search);
    } catch {
      /* storage unavailable */
    }
    return <Navigate to="/login" replace />;
  }
  if (user.mustResetPassword) return <Navigate to="/change-password" replace />;
  if (!user.mfaEnabled) return <Navigate to="/mfa-setup" replace />;
  const allowed = platform ? user.isPlatformAdmin : !permissions || can(...permissions);
  if (!allowed) {
    return (
      <AppShell>
        <Alert tone="critical">Your role does not give you access to this page. Ask your administrator if you need it.</Alert>
      </AppShell>
    );
  }
  return <AppShell>{children}</AppShell>;
}

const page = (element: JSX.Element, permissions?: string[]): JSX.Element => <Protected permissions={permissions}>{element}</Protected>;

export function App(): JSX.Element {
  const { user, loading } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={loading ? <div /> : user && user.mfaEnabled && !user.mustResetPassword ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/mfa-setup" element={loading ? <div /> : user ? (user.mustResetPassword ? <Navigate to="/change-password" replace /> : <MfaSetupPage />) : <Navigate to="/login" replace />} />

      <Route path="/" element={page(<DashboardPage />)} />

      <Route path="/residents" element={page(<ResidentsPage />, ['residents.view'])} />
      <Route path="/residents/new" element={page(<ResidentFormPage />, ['residents.manage'])} />
      <Route path="/residents/:id" element={page(<ResidentDetailPage />, ['residents.view'])} />
      <Route path="/residents/:id/edit" element={page(<ResidentFormPage />, ['residents.manage'])} />
      <Route path="/households" element={page(<HouseholdsPage />, ['residents.view'])} />
      <Route path="/households/:id" element={page(<HouseholdDetailPage />, ['residents.view'])} />
      <Route path="/registry/reports" element={page(<RegistryReportsPage />, ['residents.view', 'reports.view'])} />
      <Route path="/registry/import" element={page(<ImportPage />, ['residents.import'])} />
      <Route path="/verify/id/:token" element={<VerifyIdPage />} />
      <Route path="/verify/doc/:token" element={<VerifyDocPage />} />
      <Route path="/portal" element={<PortalEntryPage />} />
      <Route path="/portal/home" element={<PortalHomePage />} />
      <Route path="/transparency/:companyId" element={<TransparencyPage />} />
      <Route path="/verify/kp/:token" element={<VerifyDocPage kind="kp" />} />
      <Route path="/blotter" element={page(<BlotterPage />, ['blotter.view', 'blotter.create'])} />
      <Route path="/blotter/new" element={page(<BlotterNewPage />, ['blotter.create'])} />
      <Route path="/blotter/:id" element={page(<BlotterDetailPage />, ['blotter.view', 'blotter.create'])} />
      <Route path="/tanod" element={page(<TanodPage />, ['blotter.view', 'blotter.create'])} />
      <Route path="/kp" element={page(<KpListPage />, ['kp.view'])} />
      <Route path="/kp/new" element={page(<KpNewPage />, ['kp.manage'])} />
      <Route path="/kp/report" element={page(<KpReportPage />, ['kp.manage'])} />
      <Route path="/kp/:id" element={page(<KpDetailPage />, ['kp.view'])} />
      <Route path="/documents" element={page(<DocumentsPage />, ['documents.request', 'documents.approve', 'documents.release'])} />
      <Route path="/documents/new" element={page(<NewDocumentPage />, ['documents.request'])} />
      <Route path="/documents/:id" element={page(<DocumentDetailPage />, ['documents.request', 'documents.approve', 'documents.release'])} />
      <Route path="/businesses" element={page(<BusinessesPage />, ['business.view', 'business.manage'])} />
      <Route path="/setup/documents" element={page(<DocumentSetupPage />, ['barangay.manage'])} />
      <Route path="/setup/barangay" element={page(<BarangayPage />, ['barangay.view', 'barangay.manage'])} />
      <Route path="/setup/officials" element={page(<OfficialsPage />, ['officials.view', 'officials.manage'])} />
      <Route path="/setup/users" element={page(<UsersPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/roles" element={page(<RolesPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/support" element={page(<SupportAccessPage />, ['users.view', 'users.manage'])} />
      <Route path="/setup/audit" element={page(<AuditLogPage />, ['audit.view'])} />
      <Route path="/restricted-access" element={page(<RestrictedAccessPage />)} />
      <Route path="/account" element={page(<AccountPage />)} />
      <Route path="/help" element={page(<HelpPage />)} />
      <Route path="/reports" element={page(<ReportLibraryPage />)} />
      <Route path="/privacy" element={page(<PrivacyPage />, ['privacy.manage'])} />
      <Route path="/platform/consolidated" element={<Protected platform><ConsolidatedPage /></Protected>} />
      <Route path="/portal-admin/accounts" element={page(<CitizenAccountsPage />, ['portal.manage'])} />
      <Route path="/portal-admin/announcements" element={page(<AnnouncementsPage />, ['portal.manage'])} />
      <Route path="/portal-admin/feedback" element={page(<FeedbackPage />, ['portal.manage', 'documents.request', 'documents.release'])} />
      <Route path="/social/programs" element={page(<ProgramsPage />, ['social.view', 'social.manage'])} />
      <Route path="/social/programs/:id" element={page(<ProgramPage />, ['social.view', 'social.manage'])} />
      <Route path="/social/assistance" element={page(<AssistancePage />, ['social.view', 'social.manage'])} />
      <Route path="/social/distributions" element={page(<DistributionsPage />, ['social.view', 'social.manage'])} />
      <Route path="/social/distributions/:id" element={page(<DistributionPage />, ['social.view', 'social.manage', 'drrm.manage'])} />
      <Route path="/drrm/zones" element={page(<ZonesPage />, ['drrm.view', 'drrm.manage'])} />
      <Route path="/drrm/zones/:id" element={page(<ZonePage />, ['drrm.view', 'drrm.manage'])} />
      <Route path="/drrm/centers" element={page(<CentersPage />, ['drrm.view', 'drrm.manage'])} />
      <Route path="/drrm/incidents" element={page(<IncidentsPage />, ['drrm.view', 'drrm.manage'])} />
      <Route path="/drrm/incidents/:id" element={page(<IncidentPage />, ['drrm.view', 'drrm.manage'])} />
      <Route path="/drrm/fund" element={page(<FundPage />, ['drrm.view', 'drrm.manage', 'finance.view'])} />
      <Route path="/property" element={page(<PropertyPage />, ['property.view', 'property.manage'])} />
      <Route path="/property/counts" element={page(<CountsPage />, ['property.view', 'property.manage'])} />
      <Route path="/property/counts/:id" element={page(<CountPage />, ['property.view', 'property.manage'])} />
      <Route path="/property/:id" element={page(<AssetPage />, ['property.view', 'property.manage'])} />
      <Route path="/projects" element={page(<ProjectsPage />, ['projects.view', 'projects.manage'])} />
      <Route path="/projects/:id" element={page(<ProjectPage />, ['projects.view', 'projects.manage'])} />
      <Route path="/procurement" element={page(<ProcurementPage />, ['projects.view', 'projects.manage'])} />
      <Route path="/legislation" element={page(<LegislationListPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/sessions" element={page(<SessionsPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/sessions/:id" element={page(<SessionDetailPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/assemblies" element={page(<AssembliesPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/assemblies/:id" element={page(<AssemblyDetailPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/committees" element={page(<CommitteesPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/legislation/:id" element={page(<LegislationDetailPage />, ['legislation.view', 'legislation.manage'])} />
      <Route path="/treasury" element={page(<CollectionsPage />, ['finance.view', 'finance.collect', 'finance.manage'])} />
      <Route path="/treasury/deposits" element={page(<DepositsPage />, ['finance.view', 'finance.manage', 'finance.collect'])} />
      <Route path="/treasury/cashbook" element={page(<CashbookPage />, ['finance.view', 'finance.manage'])} />
      <Route path="/treasury/budget" element={page(<BudgetPage />, ['finance.view', 'finance.collect', 'finance.manage', 'finance.approve'])} />
      <Route path="/treasury/vouchers" element={page(<VouchersPage />, ['finance.view', 'finance.collect', 'finance.manage', 'finance.approve'])} />
      <Route path="/treasury/booklets" element={page(<BookletsPage />, ['finance.view', 'finance.collect', 'finance.manage'])} />
      <Route path="/treasury/income" element={page(<IncomeAdvancesPage />, ['finance.view', 'finance.manage'])} />
      <Route path="/treasury/fdp" element={page(<FdpPage />, ['finance.view', 'finance.collect', 'finance.manage', 'finance.approve', 'portal.manage'])} />
      <Route path="/vaw" element={page(<RequireGrant grant="vaw.access"><VawListPage /></RequireGrant>)} />
      <Route path="/vaw/new" element={page(<RequireGrant grant="vaw.access"><VawNewPage /></RequireGrant>)} />
      <Route path="/vaw/report" element={page(<RequireGrant grant="vaw.access"><VawReportPage /></RequireGrant>)} />
      <Route path="/vaw/:id" element={page(<RequireGrant grant="vaw.access"><VawDetailPage /></RequireGrant>)} />
      <Route path="/badac" element={page(<RequireGrant grant="badac.access"><BadacPage /></RequireGrant>)} />
      <Route path="/badac/report" element={page(<RequireGrant grant="badac.access"><BadacReportPage /></RequireGrant>)} />
      <Route path="/platform/barangays" element={<Protected platform><BarangaysPage /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
